/**
 * The time review offered right after a final Stop that COMPLETED the ticket.
 *
 * The Stop's own card cannot hold it: a completed ticket leaves the
 * technician's Dayboard as soon as the board refetches, and the card's timer
 * controls unmount with it — taking the review dialog along. So the Stop hands
 * the episode here, to one host mounted once for the whole app.
 *
 * Opening is a UX shortcut ONLY, exactly as on the card: the episode stays
 * AWAITING_REVIEW until someone reviews it, and it is still reachable later
 * from the completed ticket's card (TicketCard renders a review-only timer
 * control for a completed ticket). Nothing about acceptance depends on this.
 *
 * #3: as on the card, the server first resolves a review with nothing to
 * decide (U0/U1); only an ambiguous one — or any failure — opens the dialog.
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { getScheduleDock } from '@/components/scheduling/scheduleDock';
import { OPEN_TIMER_REVIEW_EVENT as OPEN_EVENT, openTimerReviewOutsideCard } from '@/lib/timer/interaction/reviewHost';
import { readMyDepartureReleasedReviews, type DepartureReleasedReview } from '@/lib/foh/pickup/departureActs';
import { Button } from '@/components/ui/button';
import { acceptTrivialTimerReview, trivialReviewNotice } from '@/utils/timerEpisodeReview';
import { TimerAllocationReviewDialog } from './TimerAllocationReviewDialog';

export function TimerReviewHost() {
  const [episodeId, setEpisodeId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const id = (e as CustomEvent<{ episodeId?: string }>).detail?.episodeId;
      if (!id) return;
      void acceptTrivialTimerReview(id).then((r) => {
        const notice = trivialReviewNotice(r);
        if (notice) toast.success(notice);
        if (r.kind !== 'REVIEW_REQUIRED') return;
        setEpisodeId(id);
        setOpen(true);
      });
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  return (
    <>
      <DepartureReviewNotice />
      {episodeId && <TimerAllocationReviewDialog
      workEpisodeId={episodeId}
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setEpisodeId(null);
      }}
      // No card state to re-read here: the ticket already left the board. The
      // reader's own convergence (resolved elsewhere) simply closes it.
      onServerStateChanged={() => {
        setOpen(false);
        setEpisodeId(null);
      }}
    />}
    </>
  );
}

/**
 * Oct 20 addendum (L2): a front-office person ended this technician's PAUSED work when the customer took the vehicle.
 * The ticket left every board, so the time review it created is offered here, app-wide, to its technician only
 * (my_departure_released_reviews). Reviewing goes through the established review dialog; nothing is decided here.
 */
export function DepartureReviewNotice() {
  const [rows, setRows] = useState<DepartureReleasedReview[]>([]);
  const [hidden, setHidden] = useState(false);
  const load = useCallback(() => { void readMyDepartureReleasedReviews().then(setRows).catch(() => setRows([])); }, []);
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);
  if (hidden || rows.length === 0 || typeof document === 'undefined') return null;
  const first = rows[0];
  // OCT20D-F1: the notice lives in the shared bottom dock (scheduleDock.ts), ABOVE the Report-an-Issue / Help rail.
  // Pinned to `bottom-4` itself, it was full-width on a phone and covered Report an Issue entirely (0 of 5 points
  // tappable at 375/393/667) and part of it on a tablet, while Help covered the notice's corner. In the dock it can
  // never overlap the rail, nor the schedule panels (the dock stacks them), and the dock's footprint is reserved.
  // `self-start`: below `lg` the dock right-aligns its panels; the notice stays on the LEFT, out of the bottom-right
  // corner where toasts appear from `md` up (a toast there covered its "Later" button at 820 px).
  return createPortal((
    <div role="status" data-testid="departure-review-notice" data-floating-control="departure-review-notice"
         className="pointer-events-auto order-0 self-start lg:self-auto w-full max-w-md rounded-lg border border-amber-500/50 bg-background p-3 text-sm shadow-lg">
      <p>
        Your paused work on {first.ticketNumber != null ? `RO #${first.ticketNumber}` : 'a repair'}
        {first.customerName ? ` (${first.customerName})` : ''} was ended when the customer took the vehicle
        {first.releasedByName ? ` — recorded by ${first.releasedByName}` : ''}. Your measured time is kept; review how it
        splits across the jobs.{rows.length > 1 ? ` (${rows.length} to review)` : ''}
      </p>
      <div className="mt-2 flex gap-2">
        <Button size="sm" className="min-h-11" data-testid="departure-review-open"
                onClick={() => { openTimerReviewOutsideCard(first.workEpisodeId); setTimeout(load, 3000); }}>
          Review time
        </Button>
        <Button size="sm" variant="outline" className="min-h-11" onClick={() => setHidden(true)}>Later</Button>
      </div>
    </div>
  ), getScheduleDock());
}
